import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { BehaviorSubject, Observable, distinctUntilChanged, skip, take } from 'rxjs'
import { GbItemsObserverService } from './gb-items-observer.service'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'

@UntilDestroy()
@Component({
  selector: 'gb-items-observer',
  templateUrl: './gb-items-observer.component.html',
  styleUrls: ['./gb-items-observer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbItemsObserverComponent implements OnInit, OnDestroy {
  @Input() stalePages$?: Observable<number[]>
  @Input() nthItemToObserve!: number
  @Output() onFocusEvent = new EventEmitter<number>()
  @Output() onScrollEvent = new EventEmitter<number>()

  isListening = false
  pageInView = 0
  listenToScroll$ = new BehaviorSubject<boolean>(false)

  constructor(@Inject(APP_CONFIG) protected appConfig: any, private itemsObserverService: GbItemsObserverService) {}

  ngOnInit(): void {
    if (!this.nthItemToObserve) {
      this.nthItemToObserve = this.appConfig.itemsPageSize
    }

    this.itemsObserverService
      .getPageInView()
      .pipe(take(1))
      // init pageInView with last viewed page number
      .subscribe((pageInView) => (this.pageInView = pageInView))

    this.itemsObserverService
      .getItemsToObserve()
      .pipe(skip(1), untilDestroyed(this))
      .subscribe((data) => this.observeItems(data))

    this.stalePages$
      ?.pipe(
        distinctUntilChanged((prev, curr) => prev.length === curr.length),
        untilDestroyed(this),
      )
      .subscribe((pages) => this.listenToScroll$.next(pages.length > 0))

    this.listenToScroll$.pipe(untilDestroyed(this)).subscribe((listenToScroll: boolean) => {
      if (listenToScroll && !this.isListening) {
        this.updateStaleItemsScrollListener('addEventListener')
      } else if (!listenToScroll && this.isListening) {
        this.updateStaleItemsScrollListener('removeEventListener')
      }
    })
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.scroll, true)
    this.itemsObserverService.setPageInView(this.pageInView)
  }

  observeItems(items: ElementRef[]): void {
    const threshold = 0.5
    items.forEach((item: ElementRef, index: number) => {
      if ('IntersectionObserver' in window) {
        // observe first and every nth items
        if (index % this.nthItemToObserve === 0) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.intersectionRatio >= threshold) {
                  this.pageInView = index
                  this.itemsObserverService.setPageInView(this.pageInView)
                }
              })
            },
            { threshold },
          )
          observer.observe(item.nativeElement)
        }
      }
    })
  }

  /**
   * Add / remove scroll event listener
   * @param action
   */
  updateStaleItemsScrollListener(action: keyof Window): void {
    window[action]('scroll', this.scroll, true)
    this.isListening = action === 'addEventListener'
  }

  scroll = (): void => {
    if (this.isScrollingItemList()) this.onScrollEvent.emit(this.pageInView)
  }

  isScrollingItemList(): boolean {
    return !!(
      document.activeElement === document.body || document.activeElement?.className.includes('mat-sidenav-content')
    )
  }

  @HostListener('document:visibilitychange', [])
  onFocus(): void {
    if (document.visibilityState === 'visible') {
      this.onFocusEvent.emit(this.pageInView)
    }
  }
}
